import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useEffect, useRef, useState } from 'react';

const MDAutocomplete = ({
  label,
  name,
  value,
  options,
  onChange,
  onPlayClick,
  disabled = false,
  required = false,
  showDescription = false,
  showPlayButton = false,
  isSort = true,
  labelProps = {},
}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioRef = useRef(new Audio());

  let autoOptions = options ?? [];
  if (isSort) {
    autoOptions = [...autoOptions].sort((a, b) => a.value.localeCompare(b.value));
  }

  useEffect(() => {
    const found = autoOptions.find((opt) => opt.key === value);
    setSelectedValue(found || null);
  }, [value, autoOptions]);

  const handleChange = (event, newValue) => {
    setSelectedValue(newValue);
    onChange?.({
      target: {
        name,
        value: newValue?.key ?? null,
      },
    });
  };

  const handlePlayToggle = (option) => {
    const isSame = currentlyPlaying === option.key;
    if (isSame) {
      // If same option clicked → Pause
      audioRef.current.pause();
      setCurrentlyPlaying(null);
    } else {
      if (option.audioUrl) {
        if (audioRef.current) {
          audioRef.current.pause(); // Stop any playing
        }
        const newAudio = new Audio(option.audioUrl);
        // Wait until metadata is loaded so duration is available
        newAudio.addEventListener('loadedmetadata', () => {
          newAudio.currentTime = option.key === 'Travis' ? 2 : 1; // jump to 1 seconds, Travis is slow so jump to 2 second, this is to remove VAPI thing from audio
          newAudio.play().then(() => {
            setCurrentlyPlaying(option.key);
          }).catch((error) => {
            console.error('Audio play failed:', error);
          });

          newAudio.onended = () => {
            setCurrentlyPlaying(null);
          };
          audioRef.current = newAudio;
        });
        
        
      }
    }
    onPlayClick?.(option, !isSame);
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setCurrentlyPlaying(null);
  };

  return (
    <>
      <Autocomplete
        fullWidth
        disableClearable
        options={autoOptions}
        value={selectedValue}
        onChange={handleChange}
        onClose={handleClose}
        getOptionLabel={(option) => option?.value || ''}
        isOptionEqualToValue={(option, val) => option.key === val.key}
        disabled={disabled}
        popperprops={{
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport' },
            },
            {
              name: 'offset',
              options: { offset: [0, 0] },
            },
          ],
          sx: {
            zIndex: 1300,
            position: 'absolute',
            width: '100%',
            padding: '0rem !important',
            borderColor: 'red'
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${label}${required ? ' *' : ''}`}
            InputLabelProps={labelProps}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
            }}
            size="small"
            
          />
        )}
        renderOption={(props, option) => {
          const isPlaying = currentlyPlaying === option.key;

          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                px: 2,
                py: 1.2,
                backgroundColor: isPlaying ? '#f0f0f0' : 'transparent',
                '&:hover': { backgroundColor: '#e9f1ff' },
                transition: 'background-color 0.2s ease',
                borderBottom: 'none',
              }}
            >
              <Box>
                <Box
                  fontWeight={(option.description) ? 500 : 100}
                  fontSize={(option.description) ? "0.9rem" : "0.85rem"}
                  color="text.primary"
                  sx={{ lineHeight: 1.5 }}
                >
                  {option.value}
                </Box>
                {option.description && (
                  <Box fontSize="0.85rem" color="text.secondary" mt={0.5}>
                    {option.description}
                  </Box>
                )}
              </Box>

              {option.audioUrl && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayToggle(option);
                  }}
                  sx={{
                    backgroundColor: isPlaying ? '#e57373' : '#1662C4',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: isPlaying ? '#f44336' : '#66bb6a',
                    },
                    borderRadius: '50%',
                    p: '0px',
                    ml: 'auto',
                    transition: 'all 0.3s ease',
                    animation: isPlaying ? 'bounce 0.5s ease' : 'none',
                  }}
                  size="lg"
                >
                  {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
          );
        }}
      />
      
      {/* Add Keyframe Animation for Bounce Effect */}
      <style>
        {`
          @keyframes bounce {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

MDAutocomplete.defaultProps = {
  options: [],
  onPlayClick: () => {},
  onChange: () => {},
  disabled: false,
  required: false,
  showDescription: false,
  showPlayButton: false,
  isSort: true,
  labelProps: {},
};

export default MDAutocomplete;
